import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest, uploadServiceIcon } from '../api/client'
import { Card, EmptyState, PageTitle, StatusBadge, formatDate } from '../components/Ui'
import { BackLink, DetailGrid, SummaryGrid } from '../components/AdminCommon'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'

function ServiceIconPreview({ source, name, size = 72 }) {
  return (
    <div className="service-icon-preview" style={{ width: size, height: size }}>
      {source ? <img src={source} alt={`${name} 아이콘`} /> : <span aria-hidden="true">＋</span>}
    </div>
  )
}

export default function ServiceDetailAdminV3({ id, navigate }) {
  const [item, setItem] = useState(null)
  const [categories, setCategories] = useState([])
  const [questions, setQuestions] = useState([])
  const [editor, setEditor] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const [serviceResponse, categoriesResponse, questionsResponse] = await Promise.all([
        adminRequest(`/api/v1/admin/services/${encodeURIComponent(id)}`),
        adminRequest('/api/v1/admin/categories'),
        adminRequest('/api/v1/admin/quote-questions'),
      ])
      const service = serviceResponse?.data || null
      setItem(service)
      setCategories(categoriesResponse?.data || [])
      setQuestions((questionsResponse?.data || []).filter((question) => question.serviceId === service?.id))
    } catch (nextError) {
      setError(nextError?.message || '서비스를 찾을 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id])

  const categoryLabel = useMemo(
    () => categories.find((category) => category.id === item?.categoryId)?.name || '미분류',
    [categories, item?.categoryId],
  )
  const related = useMemo(
    () => [...questions].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)),
    [questions],
  )

  const openEditor = () => {
    setNotice('')
    setEditor({ ...item, iconFile: null, iconPreview: item.iconImageUrl || '' })
  }

  const pickIcon = (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setNotice('서비스 아이콘은 이미지 파일만 등록할 수 있습니다.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setNotice('서비스 아이콘은 5MB 이하로 등록해주세요.')
      return
    }
    setEditor((current) => ({ ...current, iconFile: file, iconPreview: URL.createObjectURL(file) }))
  }

  const save = async (event) => {
    event.preventDefault()
    if (saving) return
    setSaving(true)
    setNotice('')
    try {
      let iconMediaId = editor.iconMediaId || null
      if (editor.iconFile) iconMediaId = (await uploadServiceIcon(editor.iconFile)).mediaId
      if (!iconMediaId) throw new Error('서비스 아이콘을 등록해주세요.')
      await adminRequest(`/api/v1/admin/services/${encodeURIComponent(item.publicCode || item.id)}`, {
        method: 'PUT',
        headers: { 'If-Match': String(item.version) },
        body: {
          slug: editor.slug.trim(),
          name: editor.name.trim(),
          description: editor.description?.trim() || null,
          categoryId: editor.categoryId || null,
          iconMediaId,
          status: editor.status,
          sortOrder: Number(editor.sortOrder || 0),
        },
      })
      setEditor(null)
      setNotice('서비스 정보를 수정했습니다.')
      await load()
    } catch (nextError) {
      setNotice(nextError?.message || '서비스 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (event) => {
    event.preventDefault()
    if (deleting) return
    setDeleting(true)
    setNotice('')
    try {
      await adminRequest(`/api/v1/admin/services/${encodeURIComponent(item.publicCode || item.id)}`, {
        method: 'DELETE',
        headers: { 'If-Match': String(item.version) },
      })
      setDeleteOpen(false)
      navigate('/settings/services')
    } catch (nextError) {
      setDeleteOpen(false)
      setNotice(nextError?.message || '서비스 삭제에 실패했습니다.')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) return <><BackLink onClick={() => navigate('/settings/services')} /><Card><div className="service-loading">서비스 정보를 불러오는 중입니다.</div></Card></>
  if (error || !item) return <><BackLink onClick={() => navigate('/settings/services')} /><Card><EmptyState title="서비스 조회 실패" description={error || '서비스를 찾을 수 없습니다.'} /></Card></>

  return <>
    <BackLink onClick={() => navigate('/settings/services')} />
    <PageTitle
      eyebrow="SERVICE DETAIL"
      title={item.name}
      description="사용자에게 보이는 서비스 정보와 연결된 견적 질문을 확인합니다."
      actions={<>
        <button className="button soft" onClick={openEditor}>서비스 수정</button>
        <button className="button danger-soft" onClick={() => setDeleteOpen(true)}>삭제</button>
      </>}
    />
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <SummaryGrid items={[
      { label: '상태', value: <StatusBadge status={item.status} /> },
      { label: '서비스 ID', value: <span className="mono">{item.publicCode}</span> },
      { label: '서비스 분류', value: categoryLabel },
      { label: '견적 질문', value: `${related.length}개` },
    ]} />

    <div className="dashboard-grid half service-detail-layout">
      <Card title="사용자 화면 미리보기">
        <div className="service-detail-preview-card">
          <ServiceIconPreview source={item.iconImageUrl} name={item.name} size={82} />
          <div>
            <span className="service-preview-kicker">SERVICE</span>
            <h3>{item.name}</h3>
            <p>{item.description || '서비스 설명이 아직 등록되지 않았습니다.'}</p>
          </div>
        </div>
      </Card>
      <Card title="서비스 정보">
        <DetailGrid items={[
          { label: '서비스 ID', value: item.publicCode },
          { label: 'Slug', value: item.slug },
          { label: '분류', value: categoryLabel },
          { label: '노출 순서', value: item.sortOrder },
          { label: '설명', value: item.description || '-', wide: true },
          { label: '수정일', value: formatDate(item.updatedAt, true) },
        ]} />
      </Card>
    </div>

    <Card title="연결된 견적 질문" className="table-card">
      {related.length
        ? <div className="table-scroll"><table className="data-table compact">
          <thead><tr><th>순서</th><th>질문</th><th>유형</th><th>필수</th><th>선택지</th><th>상태</th></tr></thead>
          <tbody>{related.map((question) => <tr className="clickable-row" key={question.id} onClick={() => navigate(`/settings/questions/${question.id}`)}>
            <td>{question.sortOrder}</td>
            <td><strong>{question.label}</strong><small>{question.questionKey}</small></td>
            <td>{question.type}</td>
            <td>{question.required ? '필수' : '선택'}</td>
            <td>{(question.options || []).length}개</td>
            <td><StatusBadge status={question.status} /></td>
          </tr>)}</tbody>
        </table></div>
        : <EmptyState title="연결된 견적 질문이 없습니다." description="견적 질문 관리에서 이 서비스에 필요한 질문을 추가할 수 있습니다." />}
    </Card>

    <AdminFormModal
      open={Boolean(editor)}
      title="서비스 수정"
      description="사용자 화면에 표시되는 서비스 정보와 아이콘을 수정합니다."
      onClose={() => !saving && setEditor(null)}
      onSubmit={save}
      submitLabel={saving ? '저장 중…' : '저장'}
      size="large"
    >
      {editor && <FormGrid>
        <FormField label="서비스명" required><input required value={editor.name} onChange={(event) => setEditor({ ...editor, name: event.target.value })} /></FormField>
        <FormField label="서비스 ID"><input readOnly value={item.publicCode || ''} /></FormField>
        <FormField label="Slug" required><input required value={editor.slug} onChange={(event) => setEditor({ ...editor, slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} /></FormField>
        <FormField label="서비스 분류"><select value={editor.categoryId || ''} onChange={(event) => setEditor({ ...editor, categoryId: event.target.value || null })}><option value="">미분류</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></FormField>
        <FormField label="노출 순서"><input type="number" min="0" value={editor.sortOrder ?? 0} onChange={(event) => setEditor({ ...editor, sortOrder: event.target.value })} /></FormField>
        <FormField label="상태"><select value={editor.status} onChange={(event) => setEditor({ ...editor, status: event.target.value })}><option value="ACTIVE">사용 중</option><option value="INACTIVE">사용 중지</option></select></FormField>
        <FormField label="서비스 아이콘" wide required hint="메인과 서비스 화면에 동일한 아이콘이 표시됩니다.">
          <div className="service-icon-editor">
            <ServiceIconPreview source={editor.iconPreview} name={editor.name} size={72} />
            <div className="service-icon-editor-copy">
              <strong>현재 아이콘</strong>
              <p>아이콘을 바꾸려면 새 이미지를 선택해주세요.</p>
              <label className="button soft service-file-button">아이콘 변경<input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/avif" onChange={(event) => pickIcon(event.target.files?.[0])} /></label>
            </div>
          </div>
        </FormField>
        <FormField label="서비스 설명" wide><textarea value={editor.description || ''} onChange={(event) => setEditor({ ...editor, description: event.target.value })} /></FormField>
      </FormGrid>}
    </AdminFormModal>

    <AdminFormModal
      open={deleteOpen}
      title="서비스 삭제"
      description="삭제 후에는 서비스 목록에서 복구할 수 없습니다."
      onClose={() => !deleting && setDeleteOpen(false)}
      onSubmit={remove}
      submitLabel={deleting ? '삭제 중…' : '삭제'}
      submitTone="danger-soft"
    >
      <div className="service-delete-confirm">
        <ServiceIconPreview source={item.iconImageUrl} name={item.name} size={58} />
        <div>
          <strong>{item.name}</strong>
          <p>실제 디렉터·견적·질문에 연결된 서비스는 안전을 위해 삭제가 차단됩니다. 노출만 멈추려면 ‘사용 중지’를 이용해주세요.</p>
        </div>
      </div>
    </AdminFormModal>
  </>
}
